import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import { formatLabel } from "../../utils/itemUtil";
import { formatCount, formatFullCount } from "./format";
import { SagePanel } from "./ItemCard";

const countLabel = stat => `${formatFullCount(stat.count)} ${formatLabel(stat.action)}`;

// Compact, wrapping row of every aggregate action count on an item. Long
// collections collapse behind an explicit Show all / Show less toggle so no
// action is hidden or reachable only by horizontal scrolling.
export const ActionCountRow = ({ stats = [], collapsedCount = 4, emptyText = 'No shared experiences yet' }) => {
    const [expanded, setExpanded] = useState(false);

    if (!stats.length) {
        return <Typography variant="caption" color="text.secondary">{emptyText}</Typography>;
    }

    const canCollapse = stats.length > collapsedCount;
    const visible = canCollapse && !expanded ? stats.slice(0, collapsedCount) : stats;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px' }}>
            <Box component="ul" aria-label="Shared experiences" sx={{ display: 'contents', listStyle: 'none', m: 0, p: 0 }}>
                {visible.map(stat => (
                    <Box
                        component="li"
                        key={stat.action}
                        title={countLabel(stat)}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'sage.text', fontSize: '0.8125rem', minWidth: 0 }}
                    >
                        <TaskAltIcon aria-hidden="true" sx={{ fontSize: 15 }} />
                        <Box component="strong" sx={{ color: 'text.primary' }}>{formatCount(stat.count)}</Box>
                        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>{formatLabel(stat.action)}</Box>
                    </Box>
                ))}
            </Box>
            {canCollapse ?
                <Button
                    size="small"
                    variant="text"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    sx={{ minHeight: 32, px: '6px' }}
                >
                    {expanded ? 'Show less' : `Show all ${stats.length}`}
                </Button>
            : null}
        </Box>
    );
}

// "Shared experiences" panel for item details. Accommodates any number of
// action counts; counts are never summed because people can share several.
export const SharedExperiences = ({ stats = [], userActions = [] }) => {
    return (
        <SagePanel aria-labelledby="shared-experiences-heading">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px' }}>
                <Box>
                    <Typography id="shared-experiences-heading" component="h2" variant="h2" sx={{ color: 'text.primary' }}>
                        Shared experiences
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'sage.text' }}>
                        {stats.length ? 'What people have shared about this.' : 'No one has shared an experience yet.'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', placeItems: 'center', bgcolor: 'background.paper', borderRadius: '50%', width: 44, height: 44, flexShrink: 0 }}>
                    <GroupsIcon aria-hidden="true" sx={{ fontSize: 23 }} />
                </Box>
            </Box>
            {stats.length ?
                <>
                    <Box
                        component="ul"
                        sx={{
                            listStyle: 'none',
                            m: 0,
                            mt: '24px',
                            p: 0,
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: `repeat(${Math.min(Math.max(stats.length, 1), 4)}, minmax(0, 1fr))`,
                            },
                            rowGap: '24px',
                        }}
                    >
                        {stats.map(stat => {
                            const isMine = userActions.includes(stat.action);
                            return (
                                <Box
                                    component="li"
                                    key={stat.action}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '5px',
                                        px: '15px',
                                        borderLeft: '1px solid',
                                        borderColor: 'sage.main',
                                        minWidth: 0,
                                        // First cell of each row drops its divider.
                                        '&:nth-of-type(2n+1)': { borderLeft: 0, pl: 0 },
                                        '@media (min-width: 600px)': {
                                            '&:nth-of-type(2n+1)': { borderLeft: '1px solid', pl: '15px' },
                                            '&:nth-of-type(4n+1)': { borderLeft: 0, pl: 0 },
                                        },
                                    }}
                                >
                                    <TaskAltIcon aria-hidden="true" sx={{ fontSize: 19, mb: '5px' }} />
                                    <Typography
                                        component="strong"
                                        title={formatFullCount(stat.count)}
                                        sx={{ fontSize: '2.2rem', lineHeight: 1.1, fontWeight: 650, letterSpacing: '-1px', color: 'text.primary' }}
                                    >
                                        {formatCount(stat.count)}
                                    </Typography>
                                    <Typography component="span" variant="body2" sx={{ color: 'sage.text', overflowWrap: 'anywhere' }}>
                                        {formatLabel(stat.action)}
                                        {isMine ? <Box component="span" sx={{ fontWeight: 600 }}> · including you</Box> : null}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    {stats.length > 1 ?
                        <Typography variant="caption" component="p" sx={{ color: 'sage.text', mt: '20px', mb: 0 }}>
                            People can share more than one experience.
                        </Typography>
                    : null}
                </>
            : null}
        </SagePanel>
    );
}
